import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { ArrowLeft, DollarSign, Clock, Cog, Package, ShieldCheck, AlertTriangle } from 'lucide-react';

function ConfBadge({ level }) {
  const cls = level === 'high' ? 'bg-emerald-50 text-emerald-600' : level === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-400';
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>{level}</span>;
}

export default function SiteDetailPage() {
  const { siteId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/analytics/site-detail/${siteId}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [siteId]);

  if (loading) return <div className="animate-pulse h-96 bg-slate-100 rounded-lg" />;
  if (!data) return <div className="text-center py-16 text-slate-400">Site not found</div>;

  const { site, kpis, spend_trend, product_ranking, type_comparison, machine_breakdown } = data;
  const proofColor = kpis.proof_compliance >= 70 ? 'text-emerald-600' : kpis.proof_compliance >= 40 ? 'text-amber-600' : 'text-red-600';

  const kpiCards = [
    { label: 'Total Spend', value: `₹${kpis.total_spend.toLocaleString()}`, icon: DollarSign, color: 'text-orange-600 bg-orange-50' },
    { label: 'Avg Cost/Hr', value: `₹${kpis.avg_cost_per_hour.toFixed(2)}`, icon: Clock, color: 'text-blue-600 bg-blue-50' },
    { label: 'Machines', value: kpis.machine_count, icon: Cog, color: 'text-amber-600 bg-amber-50' },
    { label: 'Products Used', value: kpis.product_count, icon: Package, color: 'text-violet-600 bg-violet-50' },
    { label: 'Change-outs', value: kpis.change_outs, icon: Clock, color: 'text-slate-600 bg-slate-100' },
    { label: 'Proof Compliance', value: `${kpis.proof_compliance.toFixed(0)}%`, icon: kpis.proof_compliance >= 70 ? ShieldCheck : AlertTriangle, color: `${proofColor} ${kpis.proof_compliance >= 70 ? 'bg-emerald-50' : kpis.proof_compliance >= 40 ? 'bg-amber-50' : 'bg-red-50'}` },
  ];

  return (
    <div data-testid="site-detail-page">
      <div className="mb-6">
        <Link to="/analytics" className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 mb-3" data-testid="back-to-analytics"><ArrowLeft size={14} />Back to Analytics</Link>
        <h1 className="page-title" data-testid="site-detail-title">{site.name}</h1>
        <div className="flex items-center gap-3 mt-1.5">
          <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded">{site.code}</span>
          <span className="text-xs text-slate-500">{site.company_name}</span>
          {site.location && <span className="text-xs text-slate-400">{site.location}</span>}
        </div>
        {(site.strata || []).length > 0 && (
          <div className="flex gap-1.5 mt-2">{site.strata.map(s => <span key={s} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{s.replace(/_/g, ' ')}</span>)}</div>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="site-kpi-cards">
        {kpiCards.map(({ label, value, icon: Icon, color }) => (
          <Card key={label} className="stat-card">
            <div className="flex items-center gap-2 mb-1"><Icon size={14} className={color.split(' ')[0]} /><p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</p></div>
            <p className="text-xl font-bold text-slate-900 font-heading">{value}</p>
          </Card>
        ))}
      </div>

      {/* Monthly Spend + Type Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <Card className="lg:col-span-2 stat-card" data-testid="site-spend-trend">
          <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Monthly Spend Trend</h3>
          {spend_trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={spend_trend}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={v => { const [y, m] = (v || '').split('-'); const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${names[parseInt(m,10)-1] || m} '${(y||'').slice(-2)}`; }} /><YAxis tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} /><Tooltip formatter={v => `₹${Number(v).toLocaleString()}`} contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }} /><Bar dataKey="spend" fill="#0F172A" radius={[4,4,0,0]} /></BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-slate-400 text-center py-12">No spend trend data</p>}
        </Card>
        <Card className="stat-card" data-testid="site-type-comparison">
          <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Casting vs Forging</h3>
          {type_comparison.length > 0 ? (
            <div className="space-y-4">
              {type_comparison.map(t => (
                <div key={t.type} className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${t.type === 'forging' ? 'bg-blue-50 text-blue-600' : 'bg-slate-200 text-slate-600'}`}>{t.label}</span>
                    <span className="text-lg font-bold text-slate-900 font-heading">₹{t.cost_per_hour.toFixed(2)}<span className="text-xs font-normal text-slate-400">/hr</span></span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                    <span>Spend: ₹{t.spend.toLocaleString()}</span>
                    <span>Trials: {t.count}</span>
                    <span>Avg Life: {t.avg_life_hours.toFixed(0)}h</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400 text-center py-8">No type data</p>}
        </Card>
      </div>

      {/* Product Ranking */}
      <Card className="stat-card mb-8" data-testid="site-product-ranking">
        <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Tooth Point Performance at This Site</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rank</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Brand</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Supplier</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Unit Price</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cost/Hr</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Avg Life</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Spend</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Trials</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Conf.</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {product_ranking.length === 0 ? <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">No product data</TableCell></TableRow> :
              product_ranking.map((p, i) => (
                <TableRow key={p.product_id} className={`hover:bg-slate-50/50 ${i === 0 && p.cost_per_hour > 0 ? 'bg-emerald-50/30' : ''}`}>
                  <TableCell className="text-sm font-bold text-slate-900">{i + 1}</TableCell>
                  <TableCell className="font-medium text-slate-900">{p.brand}</TableCell>
                  <TableCell className="text-sm text-slate-600">{p.supplier}</TableCell>
                  <TableCell><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${p.product_type === 'forging' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>{p.product_type}</span></TableCell>
                  <TableCell className="text-sm">₹{p.unit_price}</TableCell>
                  <TableCell className="text-sm font-bold text-slate-900">{p.cost_per_hour > 0 ? `₹${p.cost_per_hour.toFixed(2)}` : '-'}</TableCell>
                  <TableCell className="text-sm text-slate-600">{p.avg_life_hours > 0 ? `${p.avg_life_hours.toFixed(0)}h` : '-'}</TableCell>
                  <TableCell className="text-sm font-medium">₹{p.spend.toLocaleString()}</TableCell>
                  <TableCell className="text-sm text-slate-600">{p.count}</TableCell>
                  <TableCell><ConfBadge level={p.confidence} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Machine Breakdown */}
      <Card className="stat-card" data-testid="site-machine-breakdown">
        <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Machine Spend Breakdown</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Machine</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Serial</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Spend</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cost/Hr</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hours</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Change-outs</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Detail</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {machine_breakdown.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">No machine data</TableCell></TableRow> :
              machine_breakdown.map(m => (
                <TableRow key={m.machine_id} className="hover:bg-slate-50/50">
                  <TableCell className="font-medium text-slate-900">{m.brand} {m.model}</TableCell>
                  <TableCell className="text-sm text-slate-500">{m.serial_number}</TableCell>
                  <TableCell className="text-sm font-medium">₹{m.spend.toLocaleString()}</TableCell>
                  <TableCell className="text-sm font-bold text-slate-900">{m.cost_per_hour > 0 ? `₹${m.cost_per_hour.toFixed(2)}` : '-'}</TableCell>
                  <TableCell className="text-sm text-slate-600">{m.hours.toLocaleString()}h</TableCell>
                  <TableCell className="text-sm text-slate-600">{m.count}</TableCell>
                  <TableCell><Link to={`/analytics/machines/${m.machine_id}`} className="text-xs text-blue-600 hover:underline" data-testid={`drill-machine-${m.serial_number}`}>View</Link></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
